import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Heart, X, Phone, Save, ChevronLeft, ChevronRight, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REFRAME_CARDS = [
  { emoji: '🫂', title: 'Du är inte i centrum', body: 'De flesta är så upptagna med sig själva och sina egna tankar att de knappt märker andra. Det känns inte så — men det är oftast sant.', tags: ['social'] },
  { emoji: '👀', title: 'Blickar är ofta ”förbi”', body: 'Vi misstolkar ofta en sveptitt eller en blick mot något bakom oss som en stirrande blick. Hjärnan är extra känslig när vi känner oss utsatta.', tags: ['social'] },
  { emoji: '💛', title: 'Det här går över', body: 'Känslan är en våg. Den stiger, toppar och sjunker undan. Du har klarat den här exakta känslan varenda gång hittills.', tags: ['all'] },
  { emoji: '🌸', title: 'De bryr sig inte', body: 'Om någon faktiskt märkte dig — de glömmer det inom 10 minuter. De har fullt upp med sitt eget kaos, precis som du.', tags: ['social'] },
  { emoji: '🧠', title: 'Din hjärna skyddar dig', body: 'Känslan (observerad, stressad, oduglig) är en gammal överlevnadsmekanism. Den är överaktiv, inte fel. Tack, hjärnan — men idag behöver jag inte det skyddet.', tags: ['all'] },
  { emoji: '🐕', title: 'Du får vara här', body: 'Precis så som du är, precis just nu. Din plats i rummet är lika giltig som allas andras. Inget behöver bevisas.', tags: ['all'] },
  { emoji: '🎭', title: 'Ingen ser ”fel” som du tror', body: 'Det du oroar dig över — att du ser konstig ut, rör dig konstigt, bär fel — syns nästan aldrig utifrån. Ditt inre är dolt för dem.', tags: ['social'] },
  { emoji: '🍃', title: 'Andas ut långsamt', body: 'Långsamma utandningar lugnar nervsystemet direkt. Blås ut som om du blåser på varm te.', tags: ['all'] },
  // Stress-specific
  { emoji: '🌪️', title: 'Allt behöver inte göras idag', body: 'Din hjärna säger att allt är akut. Det är en lögn stressen berättar. Det MESTA kan vänta till imorgon, nästa vecka, eller aldrig.', tags: ['stress'] },
  { emoji: '🎯', title: 'En sak i taget', body: 'Du kan inte göra fem saker samtidigt. Ingen kan. Välj EN. Gör den i 10 minuter. Sen får du bestämma nästa.', tags: ['stress'] },
  { emoji: '⏳', title: 'Klockan tickar lika för alla', body: 'Folk runt dig hinner inte heller allt. De visar bara det som gick bra. Du jämför ditt inre kaos med deras yttre fasad — orättvist mot dig själv.', tags: ['stress', 'inadequate'] },
  { emoji: '💆', title: 'Du får pausa utan att försvinna', body: 'Pauser är inte lathet. De är en del av arbetet. Även 3 minuter räknas. Världen fortsätter snurra utan din konstanta vakenhet.', tags: ['stress'] },
  // Inadequacy-specific
  { emoji: '🫧', title: 'Du är inte odugelig — du är trött', body: 'Hjärnan blandar ihop ”jag orkar inte just nu” med ”jag duger inte”. Det är två helt olika saker. Trötthet är tillfällig. Ditt värde är inte.', tags: ['inadequate'] },
  { emoji: '🌱', title: 'Du gör mer än du tror', body: 'Allt du gjort idag — steg upp, åt något, borstade kanske tänderna, öppnade den här appen — är handlingar. De räknas. Du räknas.', tags: ['inadequate'] },
  { emoji: '🪞', title: 'Jämför dig med dig själv', body: 'Inte med någon annans bästa dag. Inte med Instagram. Inte med vad du ”borde” ha gjort vid 25. Jämför dig med du igår. Det räcker.', tags: ['inadequate'] },
  { emoji: '💝', title: 'Du skulle aldrig säga så till en vän', body: '”Du är oduglig, du hinner inget, du är sämst.” — hade du sagt det till din bästa vän? Nej. Säg samma sak till dig själv som du skulle sagt till henne.', tags: ['inadequate'] },
  // Alien/ojämnhet-specific
  { emoji: '👽', title: 'Att känna sig utomjording är en riktig sak', body: 'Det finns ett ord för det: <b>”alienation”</b>. Många neurodivergenta, introverta eller kreativa människor känner det. Det betyder inte att du är fel — det betyder att du upplever världen annorlunda än genomsnittet. Det är en skillnad, inte en brist.', tags: ['alien'] },
  { emoji: '🪞', title: 'Loopen du känner är verklig — och bryts', body: 'Känna attityd → spänna musklerna → prata osammanhängande → känna sig ännu konstigare. Det är en spiral. Den bryts när du <b>bryter en del</b>: andas ut, pausa 2 sekunder, säg mindre. Tystnad är inte konstigt. Det är vila.', tags: ['alien'] },
  { emoji: '🗣️', title: 'När orden fastnar — säg mindre, inte mer', body: 'Hjärnan tror att mer ord = mer kontroll. Fel. <b>Korta meningar</b> är din bästa vän. "Mm." "Jag förstår." "Låt mig tänka." Du behöver inte prestera flyt.', tags: ['alien'] },
  { emoji: '🌍', title: 'De andra kanske också spelar roll', body: 'De som verkar ha en "attityd" är oftast själva stressade, trötta, ointresserade av något helt annat. Deras ansikte speglar inte dig. De är i sin egen film.', tags: ['alien', 'social'] },
  { emoji: '🎭', title: 'Du får sluta ”tolka rummet”', body: 'Din hjärna försöker läsa allas ansikten och mikrouttryck för att förutse avvisning. Det är utmattande och oftast fel. <b>Du får slippa vara tolk.</b> Ha din blick på en vägg, en bok, din telefon. Du har rätt att inte avläsa.', tags: ['alien', 'social'] },
  { emoji: '🌱', title: '”Konstig” är ofta bara ”jag är mig själv”', body: 'Det som känns konstigt för dig är ofta helt osynligt utåt. Och <b>det som syns</b> är ofta det människor dras till — äkthet, särdrag, att du inte är en kopia. Du är inte en bugg. Du är en feature.', tags: ['alien'] },
];

const DISTRACTION_TASKS = [
  '🔵 Räkna alla blåa saker du ser runt dig',
  '🎵 Nynna en låt tyst i huvudet från start till slut',
  '🔤 Tänk ett djur på varje bokstav i alfabetet (A, B, C…)',
  '👣 Känn dina fötter i skorna. Rör tårna 3 gånger.',
  '🧮 Räkna baklänges från 100 med 7 i taget',
  '🌡️ Känn temperaturen på luften mot dina händer',
  '🎨 Hitta 3 saker i din favoritfärg',
  '📏 Gissa höjden på 3 saker du ser. Kolla sen efteråt om du kan.',
];

const TRIGGERS = [
  { id: 'crowd',      label: 'I folkmassa',              emoji: '👥' },
  { id: 'transit',    label: 'På buss/tåg',              emoji: '🚌' },
  { id: 'shop',       label: 'I affären',                emoji: '🛒' },
  { id: 'work',       label: 'På jobbet/skolan',         emoji: '💼' },
  { id: 'walk',       label: 'På promenad',              emoji: '🚶' },
  { id: 'alien',      label: 'Känner mig som utomjord.', emoji: '👽' },
  { id: 'stress',     label: 'Jag hinner inte',          emoji: '🌪️' },
  { id: 'inadequate', label: 'Känner mig oduglig',       emoji: '🫧' },
  { id: 'other',      label: 'Annat',                    emoji: '✨' },
];

export default function CalmPanel({ open, onClose }) {
  const [step, setStep] = useState('home'); // home | breathing | grounding | reframe | distract | contacts | done
  const [trigger, setTrigger] = useState(null);
  const [techniques, setTechniques] = useState([]);
  const [helpful, setHelpful] = useState(null);
  const [note, setNote] = useState('');
  const [contacts, setContacts] = useState([]);
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');

  // Trygg plats (safe-place photos)
  const [safePlaces, setSafePlaces] = useState([]);
  const [safePlaceIdx, setSafePlaceIdx] = useState(0);
  const [newPlaceLabel, setNewPlaceLabel] = useState('');
  const [newPlacePhoto, setNewPlacePhoto] = useState(null);
  const [placeBusy, setPlaceBusy] = useState(false);

  // Reframe cards — filtered to match current trigger
  const [cardIdx, setCardIdx] = useState(0);
  const relevantCards = React.useMemo(() => {
    const tagMap = {
      crowd: 'social', transit: 'social', shop: 'social', walk: 'social', work: 'social',
      stress: 'stress', inadequate: 'inadequate',
      alien: 'alien',
    };
    const tag = trigger ? tagMap[trigger] : null;
    if (!tag) return REFRAME_CARDS;
    return REFRAME_CARDS.filter((c) => (c.tags || []).includes(tag) || (c.tags || []).includes('all'));
  }, [trigger]);
  const currentCard = relevantCards[cardIdx % relevantCards.length];

  // Priority reset (stress)
  const [priorityOne, setPriorityOne] = useState('');
  const [priorityDone, setPriorityDone] = useState(false);

  // Box breathing
  const [breathPhase, setBreathPhase] = useState('in'); // in | hold1 | out | hold2
  const [breathCount, setBreathCount] = useState(4);
  const breathTimerRef = useRef(null);

  // Grounding 5-4-3-2-1
  const groundingSteps = [
    { n: 5, label: 'saker du ser', emoji: '👀' },
    { n: 4, label: 'saker du kan höra', emoji: '👂' },
    { n: 3, label: 'saker du kan känna', emoji: '🫳' },
    { n: 2, label: 'saker du kan lukta', emoji: '👃' },
    { n: 1, label: 'sak du kan smaka', emoji: '👅' },
  ];
  const [groundIdx, setGroundIdx] = useState(0);
  const [groundCount, setGroundCount] = useState(0);

  // Distraction
  const [distractIdx, setDistractIdx] = useState(0);

  const addTech = (t) => setTechniques((arr) => arr.includes(t) ? arr : [...arr, t]);

  // Load contacts when opening
  const loadContacts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/calm/contacts`, { withCredentials: true });
      setContacts(data || []);
    } catch { /* ignore */ }
  }, []);

  // Load safe places
  const loadSafePlaces = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/calm/safe-places`, { withCredentials: true });
      setSafePlaces(data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (open) {
      loadContacts();
      loadSafePlaces();
      // Reset per session
      setStep('home');
      setTrigger(null);
      setTechniques([]);
      setHelpful(null);
      setNote('');
      setCardIdx(Math.floor(Math.random() * REFRAME_CARDS.length));
      setGroundIdx(0); setGroundCount(0);
      setDistractIdx(Math.floor(Math.random() * DISTRACTION_TASKS.length));
      setSafePlaceIdx(0);
    }
  }, [open, loadContacts, loadSafePlaces]);

  // Box breathing cycle
  useEffect(() => {
    if (step !== 'breathing') { if (breathTimerRef.current) clearInterval(breathTimerRef.current); return; }
    setBreathPhase('in'); setBreathCount(4);
    const phases = ['in', 'hold1', 'out', 'hold2'];
    let pIdx = 0;
    let count = 4;
    breathTimerRef.current = setInterval(() => {
      count -= 1;
      if (count <= 0) {
        pIdx = (pIdx + 1) % phases.length;
        count = 4;
        setBreathPhase(phases[pIdx]);
      }
      setBreathCount(count === 0 ? 4 : count);
    }, 1000);
    return () => { if (breathTimerRef.current) clearInterval(breathTimerRef.current); };
  }, [step]);

  const saveSession = async () => {
    try {
      await axios.post(`${API}/calm/session`,
        { techniques, note, trigger, helpful },
        { withCredentials: true });
      toast.success('💛 Sparat. Du gjorde något snällt för dig själv.');
    } catch { /* ignore */ }
    onClose();
  };

  const addContact = async () => {
    const name = newContactName.trim();
    const phone = newContactPhone.trim();
    if (!name || !phone) { toast.error('Fyll i båda 🌸'); return; }
    try {
      await axios.post(`${API}/calm/contacts`, { name, phone }, { withCredentials: true });
      setNewContactName(''); setNewContactPhone('');
      loadContacts();
    } catch { toast.error('Kunde inte spara'); }
  };

  const deleteContact = async (id) => {
    try {
      await axios.delete(`${API}/calm/contacts/${id}`, { withCredentials: true });
      loadContacts();
    } catch { /* ignore */ }
  };

  const handlePlacePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setPlaceBusy(true);
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1280;
        let w = img.width;
        let h = img.height;
        if (w > maxW) { h = Math.round(h * (maxW / w)); w = maxW; }
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        let url = c.toDataURL('image/jpeg', 0.72);
        if (url.length > 2_400_000) url = c.toDataURL('image/jpeg', 0.55);
        setNewPlacePhoto(url);
        setPlaceBusy(false);
      };
      img.onerror = () => setPlaceBusy(false);
      img.src = reader.result;
    };
    reader.onerror = () => setPlaceBusy(false);
    reader.readAsDataURL(file);
  };

  const addSafePlace = async () => {
    if (!newPlaceLabel.trim() || !newPlacePhoto) return;
    setPlaceBusy(true);
    try {
      await axios.post(`${API}/calm/safe-places`, {
        label: newPlaceLabel.trim(),
        photo: newPlacePhoto,
      }, { withCredentials: true });
      setNewPlaceLabel('');
      setNewPlacePhoto(null);
      loadSafePlaces();
    } catch { /* silent */ }
    finally { setPlaceBusy(false); }
  };

  if (!open) return null;

  // UI helpers
  const breathLabel = breathPhase === 'in' ? 'Andas in långsamt…'
    : breathPhase === 'hold1' ? 'Håll…'
    : breathPhase === 'out' ? 'Andas ut mjukt…'
    : 'Håll…';
  const breathScale = breathPhase === 'in' ? 1.4
    : breathPhase === 'hold1' ? 1.4
    : breathPhase === 'out' ? 0.9
    : 0.9;

  return (
    <div
      className="fixed inset-0 z-[220] bg-gradient-to-br from-[#E8F7FF] via-[#F5EEFF] to-[#FFE8F5] overflow-auto p-4"
      data-testid="calm-panel"
    >
      <div className="max-w-md mx-auto py-4 space-y-4">
        {/* Header with close */}
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-[#C8F0FF] border-2 border-black flex items-center justify-center text-xl">🌿</div>
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase opacity-60">Lugnt just nu</p>
            <p className="text-sm">Du är trygg här 💛 Inget behöver bevisas.</p>
          </div>
          <button onClick={onClose} className="neo-btn p-2 bg-white" data-testid="calm-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === 'home' && (
          <>
            <div className="neo-card p-4 bg-white text-center">
              <p className="text-4xl mb-2">🐕💛</p>
              <p className="font-bold font-['Fredoka'] text-lg">Hej. Andas in lite lätt.</p>
              <p className="text-xs text-gray-600 mt-1">
                Oavsett om du känner dig <b>observerad</b>, <b>stressad</b>, eller <b>oduglig</b> just nu — det är samma nervsystem som går på högvarv. Du är inte konstig. Hjärnan är bara överkänslig.
              </p>
            </div>

            <div className="neo-card p-3 bg-[#FFFEF5]">
              <p className="text-xs font-bold mb-2 opacity-70">Vad känner du just nu?</p>
              <div className="flex flex-wrap gap-1.5">
                {TRIGGERS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setTrigger(t.id); setCardIdx(0); }}
                    className={`neo-btn px-2 py-1 text-xs flex items-center gap-1 ${trigger === t.id ? 'bg-[#C8B6FF]' : 'bg-white'}`}
                    data-testid={`calm-trigger-${t.id}`}
                  ><span>{t.emoji}</span>{t.label}</button>
                ))}
              </div>
            </div>

            <p className="text-xs font-bold text-center opacity-60">Välj vad som känns rätt just nu:</p>

            <div className="grid grid-cols-2 gap-2">
              <ToolBtn onClick={() => { addTech('breathing'); setStep('breathing'); }} emoji="🌬️" title="Box-andning" sub="1 min lugn" testid="calm-go-breathing" bg="#E8F7FF" />
              <ToolBtn onClick={() => { addTech('grounding'); setStep('grounding'); }} emoji="👀" title="5-4-3-2-1" sub="Sinnena" testid="calm-go-grounding" bg="#B9FBC0" />
              <ToolBtn onClick={() => { addTech('reframe'); setStep('reframe'); }} emoji="💭" title="Realitetscheck" sub="Mjuka påminnelser" testid="calm-go-reframe" bg="#FFE8A3" />
              <ToolBtn onClick={() => { addTech('rescue'); setStep('rescue'); }} emoji="🗣️" title="Räddningsfraser" sub="När orden fastnar" testid="calm-go-rescue" bg="#C8F0FF" />
              <ToolBtn onClick={() => { addTech('priority'); setStep('priority'); }} emoji="🎯" title="Prioritera om" sub="Hitta EN sak" testid="calm-go-priority" bg="#FFD6A5" />
              <ToolBtn onClick={() => { addTech('distract'); setStep('distract'); }} emoji="🧩" title="Distraktion" sub="Bort från tanken" testid="calm-go-distract" bg="#FFC8DD" />
              <ToolBtn onClick={() => { addTech('safeplace'); setStep('safeplace'); }} emoji="🏞️" title="Trygg plats" sub="Titta på foton" testid="calm-go-safeplace" bg="#D4E4FF" />
              <ToolBtn onClick={() => { addTech('contacts'); setStep('contacts'); }} emoji="📞" title="Trygg kontakt" sub="Ring någon" testid="calm-go-contacts" bg="#C8B6FF" />
            </div>
            <button onClick={() => setStep('done')} className="neo-btn w-full py-2 bg-[#F5EEFF] text-xs font-bold" data-testid="calm-go-done">
              ✅ Klar & spara
            </button>

            <div className="neo-card p-3 bg-[#FFE8E8] text-xs">
              <p><b>🆘 Akut?</b> Ring <a href="tel:1177" className="underline font-bold">1177</a> (vård) · <a href="tel:90101" className="underline font-bold">901 01</a> (Mind självmordslinjen) · <a href="tel:112" className="underline font-bold">112</a> (akut fara).</p>
            </div>
          </>
        )}

        {step === 'breathing' && (
          <div className="neo-card p-6 bg-white text-center" data-testid="calm-breathing">
            <p className="text-xs font-bold uppercase opacity-60 mb-1">4·4·4·4 box-andning</p>
            <p className="text-sm mb-6">Följ cirkeln. Gör detta 4 varv.</p>
            <div className="relative h-56 flex items-center justify-center">
              <div
                className="w-40 h-40 rounded-full bg-gradient-to-br from-[#FFC8DD] to-[#C8B6FF] border-4 border-black flex items-center justify-center transition-transform duration-[1000ms] ease-in-out"
                style={{ transform: `scale(${breathScale})` }}
              >
                <span className="text-6xl font-bold font-mono tabular-nums text-white drop-shadow">{breathCount}</span>
              </div>
            </div>
            <p className="text-lg font-bold font-['Fredoka'] mt-4">{breathLabel}</p>
            <p className="text-[11px] opacity-60 mt-2">Utandning är nyckeln — den lugnar nervsystemet direkt.</p>
            <button onClick={() => setStep('home')} className="neo-btn mt-4 px-4 py-2 bg-[#B9FBC0] text-sm" data-testid="calm-breathing-done">
              Klar med andning ✓
            </button>
          </div>
        )}

        {step === 'grounding' && (
          <div className="neo-card p-5 bg-white text-center" data-testid="calm-grounding">
            <p className="text-xs font-bold uppercase opacity-60 mb-1">5·4·3·2·1 grounding</p>
            <p className="text-5xl my-3">{groundingSteps[groundIdx].emoji}</p>
            <p className="text-2xl font-bold font-['Fredoka']">
              {groundingSteps[groundIdx].n - groundCount} {groundingSteps[groundIdx].label}
            </p>
            <p className="text-[11px] opacity-60 mt-1">Räkna tyst eller högt, ett i taget.</p>
            <button
              onClick={() => {
                if (groundCount + 1 >= groundingSteps[groundIdx].n) {
                  if (groundIdx + 1 >= groundingSteps.length) {
                    toast.success('🫂 Bra! Du är här nu.');
                    setStep('home');
                  } else {
                    setGroundIdx(groundIdx + 1);
                    setGroundCount(0);
                  }
                } else {
                  setGroundCount(groundCount + 1);
                }
              }}
              className="neo-btn mt-4 px-6 py-3 bg-[#FFC8DD] font-bold"
              data-testid="calm-ground-next"
            >
              En till ✓
            </button>
            <div className="flex justify-center gap-1 mt-3">
              {groundingSteps.map((_, i) => (
                <span key={i} className={`w-2 h-2 rounded-full ${i < groundIdx ? 'bg-[#B9FBC0]' : i === groundIdx ? 'bg-[#FFC8DD]' : 'bg-gray-200'}`} />
              ))}
            </div>
          </div>
        )}

        {step === 'reframe' && (
          <div className="neo-card p-5 bg-white" data-testid="calm-reframe">
            <p className="text-xs font-bold uppercase opacity-60 mb-3 text-center">
              Realitetscheck {trigger && <span className="opacity-60">· för "{TRIGGERS.find(t => t.id === trigger)?.label}"</span>}
            </p>
            <div className="min-h-[180px] flex flex-col items-center justify-center text-center">
              <p className="text-5xl mb-2">{currentCard.emoji}</p>
              <p className="text-lg font-bold font-['Fredoka'] mb-2">{currentCard.title}</p>
              <p className="text-sm text-gray-700 leading-relaxed px-2">{currentCard.body}</p>
            </div>
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => setCardIdx((cardIdx - 1 + relevantCards.length) % relevantCards.length)}
                className="neo-btn p-2 bg-white"
                data-testid="calm-reframe-prev"
              ><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-[10px] opacity-60">{(cardIdx % relevantCards.length) + 1} / {relevantCards.length}</span>
              <button
                onClick={() => setCardIdx((cardIdx + 1) % relevantCards.length)}
                className="neo-btn p-2 bg-white"
                data-testid="calm-reframe-next"
              ><ChevronRight className="w-4 h-4" /></button>
            </div>
            <button onClick={() => setStep('home')} className="neo-btn mt-3 w-full py-2 bg-[#B9FBC0] text-sm" data-testid="calm-reframe-done">
              Tillbaka
            </button>
          </div>
        )}

        {step === 'rescue' && (
          <div className="neo-card p-4 bg-white space-y-3" data-testid="calm-rescue">
            <p className="text-xs font-bold uppercase opacity-60 text-center">Räddningsfraser 🗣️</p>
            <p className="text-xs text-gray-600 text-center">
              När orden fastnar — <b>säg mindre, inte mer</b>. Använd en av dessa.
            </p>
            <div className="space-y-2">
              {[
                { emoji: '🤔', text: '"Låt mig tänka en sekund."', note: 'Paus. Andas. Det är OK.' },
                { emoji: '👂', text: '"Jag förstår."', note: 'Kort. Räcker. Behöver inte mer.' },
                { emoji: '🌀', text: '"Jag kom av mig — vad sa du senast?"', note: 'Folk blir aldrig arga. De hjälper gärna.' },
                { emoji: '💬', text: '"Jag återkommer om det där."', note: 'Perfekt när du inte orkar just nu.' },
                { emoji: '🫥', text: '"Mm." eller bara en nick.', note: 'Du behöver inte prata för att vara artig.' },
                { emoji: '🚪', text: '"Jag behöver en kort paus. Strax tillbaka."', note: 'Gå till toan, drick vatten, andas.' },
                { emoji: '💡', text: '"Jag är trött idag, ursäkta om jag är lite seg."', note: 'Ärlighet avväpnar både dig och dem.' },
                { emoji: '🐌', text: '"Sakta ner, jag hinner inte med."', note: 'Du får be andra prata långsammare.' },
              ].map((p, i) => (
                <div key={i} className="neo-card p-2.5 bg-[#FFFEF5]" data-testid={`calm-rescue-${i}`}>
                  <p className="text-sm font-bold">{p.emoji} {p.text}</p>
                  <p className="text-[11px] text-gray-600 italic mt-0.5">{p.note}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-500 text-center italic px-2">
              Tystnad är inte konstigt. Tystnad är vila. Du behöver inte fylla luften. 🌸
            </p>
            <button onClick={() => setStep('home')} className="neo-btn w-full py-2 bg-[#B9FBC0] text-sm" data-testid="calm-rescue-done">
              Tillbaka
            </button>
          </div>
        )}

        {step === 'priority' && (
          <div className="neo-card p-5 bg-white space-y-3" data-testid="calm-priority">
            <p className="text-xs font-bold uppercase opacity-60 text-center">Prioritera om</p>
            <p className="text-sm text-center text-gray-700">
              När allt känns viktigt är inget viktigt. Låt oss hitta <b>EN</b> sak just nu.
            </p>
            {!priorityDone ? (
              <>
                <div className="neo-card p-3 bg-[#FFFEF5] space-y-2">
                  <p className="text-xs font-bold">🎯 Vad är en sak som, om bara den blev gjord idag, skulle göra dig lite lugnare?</p>
                  <textarea
                    value={priorityOne}
                    onChange={(e) => setPriorityOne(e.target.value)}
                    placeholder="t.ex. skicka det där mejlet, diska, ringa mamma…"
                    rows={2}
                    className="w-full neo-input text-sm"
                    data-testid="calm-priority-input"
                  />
                </div>
                <button
                  onClick={() => priorityOne.trim() && setPriorityDone(true)}
                  disabled={!priorityOne.trim()}
                  className="neo-btn w-full py-3 bg-[#C8B6FF] font-bold text-white"
                  data-testid="calm-priority-commit"
                >
                  Det här är min enda uppgift nu →
                </button>
                <div className="neo-card p-3 bg-[#F5EEFF] text-[11px] leading-relaxed">
                  <p className="font-bold mb-1">💛 Kom ihåg:</p>
                  <ul className="space-y-1 list-disc list-inside opacity-80">
                    <li>Allt annat får vänta utan att världen slutar snurra</li>
                    <li>Du kan ta 10 min paus först om det hjälper</li>
                    <li>Även HALV-gjort räknas. Perfekt finns inte.</li>
                  </ul>
                </div>
              </>
            ) : (
              <>
                <div className="neo-card p-4 bg-gradient-to-br from-[#B9FBC0] to-[#FFE8A3] text-center">
                  <p className="text-[10px] font-bold uppercase opacity-70">Ditt EN-sak-uppdrag</p>
                  <p className="text-lg font-bold font-['Fredoka'] mt-1">{priorityOne}</p>
                </div>
                <p className="text-xs text-center text-gray-600 italic px-2">
                  Resten av listan finns kvar imorgon. Du har bara det här att tänka på just nu 🌸
                </p>
                <button onClick={() => { setPriorityDone(false); setPriorityOne(''); }} className="neo-btn w-full py-2 bg-white text-xs">
                  Byt uppgift
                </button>
              </>
            )}
            <button onClick={() => setStep('home')} className="neo-btn w-full py-2 bg-white text-sm">Tillbaka</button>
          </div>
        )}

        {step === 'distract' && (
          <div className="neo-card p-5 bg-white text-center" data-testid="calm-distract">
            <p className="text-xs font-bold uppercase opacity-60 mb-3">Bryt tankespåret — prova detta</p>
            <p className="text-lg font-bold font-['Fredoka'] leading-snug min-h-[80px] flex items-center justify-center px-2">
              {DISTRACTION_TASKS[distractIdx]}
            </p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setDistractIdx((distractIdx + 1) % DISTRACTION_TASKS.length)}
                className="neo-btn flex-1 py-2 bg-white"
                data-testid="calm-distract-next"
              >Nästa 🎲</button>
              <button onClick={() => setStep('home')} className="neo-btn flex-1 py-2 bg-[#B9FBC0] font-bold" data-testid="calm-distract-done">
                Klar ✓
              </button>
            </div>
          </div>
        )}

        {step === 'contacts' && (
          <div className="neo-card p-4 bg-white space-y-3" data-testid="calm-contacts">
            <p className="text-xs font-bold uppercase opacity-60">Trygga kontakter</p>
            {contacts.length === 0 && (
              <p className="text-[11px] text-gray-500 italic">Inga sparade än. Lägg till någon du kan ringa när allt känns mycket 💛</p>
            )}
            {contacts.map((c) => (
              <div key={c.id} className="neo-card p-2.5 bg-[#FFFEF5] flex items-center gap-2" data-testid={`calm-contact-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{c.name}</p>
                  <p className="text-[11px] opacity-60 truncate">{c.phone}</p>
                </div>
                <a href={`tel:${c.phone.replace(/\s+/g, '')}`} className="neo-btn p-2 bg-[#B9FBC0]" data-testid={`calm-call-${c.id}`}>
                  <Phone className="w-4 h-4" />
                </a>
                <button onClick={() => deleteContact(c.id)} className="neo-btn p-2 bg-white">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            <div className="neo-card p-2 bg-[#F5EEFF] space-y-1.5">
              <p className="text-[11px] font-bold">Lägg till ny</p>
              <input
                placeholder="Namn"
                value={newContactName}
                onChange={(e) => setNewContactName(e.target.value)}
                className="neo-input text-sm w-full"
                data-testid="calm-new-contact-name"
              />
              <input
                placeholder="Telefon"
                value={newContactPhone}
                onChange={(e) => setNewContactPhone(e.target.value)}
                className="neo-input text-sm w-full"
                data-testid="calm-new-contact-phone"
              />
              <button onClick={addContact} className="neo-btn w-full py-1.5 bg-[#B9FBC0] text-xs font-bold" data-testid="calm-add-contact">
                <Plus className="w-3 h-3 inline" /> Spara kontakt
              </button>
            </div>
            <button onClick={() => setStep('home')} className="neo-btn w-full py-2 bg-white text-sm">Tillbaka</button>
          </div>
        )}

        {step === 'safeplace' && (
          <div className="neo-card p-4 bg-white space-y-3" data-testid="calm-safeplace">
            <p className="text-xs font-bold uppercase opacity-60">Trygg plats 🏞️</p>
            {safePlaces.length === 0 ? (
              <p className="text-[11px] text-gray-500 italic">
                Lägg till foton av platser där du känner dig trygg — havet, en park, ditt rum, mormors kök. När du tittar på dem nu lugnar din nervsystem.
              </p>
            ) : (
              <>
                <div className="relative neo-card p-1 bg-[#F0F8FF]" data-testid="calm-safeplace-viewer">
                  <img
                    src={safePlaces[safePlaceIdx % safePlaces.length].photo}
                    alt={safePlaces[safePlaceIdx % safePlaces.length].label}
                    className="w-full max-h-72 object-cover rounded"
                  />
                  <div className="absolute bottom-2 left-2 bg-white/90 px-2 py-1 rounded-md text-xs font-bold">
                    🏞️ {safePlaces[safePlaceIdx % safePlaces.length].label}
                  </div>
                </div>
                {safePlaces[safePlaceIdx % safePlaces.length].note && (
                  <p className="text-[11px] italic opacity-70 px-1">📝 {safePlaces[safePlaceIdx % safePlaces.length].note}</p>
                )}
                {safePlaces.length > 1 && (
                  <div className="flex justify-between items-center text-xs">
                    <button
                      onClick={() => setSafePlaceIdx((i) => (i - 1 + safePlaces.length) % safePlaces.length)}
                      className="neo-btn px-3 py-1 bg-white"
                      data-testid="calm-safeplace-prev"
                    >
                      ← Föregående
                    </button>
                    <span className="opacity-60">{(safePlaceIdx % safePlaces.length) + 1}/{safePlaces.length}</span>
                    <button
                      onClick={() => setSafePlaceIdx((i) => (i + 1) % safePlaces.length)}
                      className="neo-btn px-3 py-1 bg-white"
                      data-testid="calm-safeplace-next"
                    >
                      Nästa →
                    </button>
                  </div>
                )}
                <p className="text-[11px] text-center opacity-70 italic">
                  Andas in 4. Andas ut 6. Bara titta. Du är trygg här. 💗
                </p>
              </>
            )}

            {/* Add new safe place */}
            <div className="neo-card p-2 bg-[#F5EEFF] space-y-1.5">
              <p className="text-[11px] font-bold">Lägg till en plats</p>
              <input
                placeholder="Namn (t.ex. mormors hus)"
                value={newPlaceLabel}
                onChange={(e) => setNewPlaceLabel(e.target.value)}
                className="neo-input text-sm w-full"
                data-testid="calm-new-place-label"
              />
              {newPlacePhoto ? (
                <div className="relative neo-card p-1 bg-white">
                  <img src={newPlacePhoto} alt="ny" className="max-h-32 w-full rounded object-cover" />
                  <button
                    type="button"
                    onClick={() => setNewPlacePhoto(null)}
                    className="absolute -top-2 -right-2 bg-white rounded-full shadow border-2 border-red-300 p-1"
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ) : (
                <label className="neo-btn flex items-center justify-center gap-1 py-2 bg-[#D4E4FF] text-xs font-bold cursor-pointer border-dashed border-2 border-[#4A8BC7]/40">
                  📸 Välj foto
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handlePlacePhoto(e)}
                    className="hidden"
                    data-testid="calm-new-place-photo-input"
                  />
                </label>
              )}
              <button
                onClick={addSafePlace}
                disabled={placeBusy || !newPlaceLabel.trim() || !newPlacePhoto}
                className="neo-btn w-full py-1.5 bg-[#B9FBC0] text-xs font-bold disabled:opacity-50"
                data-testid="calm-add-place"
              >
                {placeBusy ? '...' : '+ Spara plats'}
              </button>
            </div>
            <button onClick={() => setStep('home')} className="neo-btn w-full py-2 bg-white text-sm">Tillbaka</button>
          </div>
        )}

        {step === 'done' && (
          <div className="neo-card p-4 bg-white space-y-3" data-testid="calm-done">
            <p className="font-bold font-['Fredoka'] text-lg text-center">Hur gick det?</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setHelpful(true)} className={`neo-btn py-3 ${helpful === true ? 'bg-[#B9FBC0]' : 'bg-white'}`} data-testid="calm-helpful-yes">
                💛 Lite bättre
              </button>
              <button onClick={() => setHelpful(false)} className={`neo-btn py-3 ${helpful === false ? 'bg-[#FFE8A3]' : 'bg-white'}`} data-testid="calm-helpful-no">
                🌧️ Samma känsla
              </button>
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Vill du skriva något? (helt valfritt)"
              rows={3}
              className="w-full neo-input text-sm"
              data-testid="calm-note"
            />
            <button onClick={saveSession} className="neo-btn w-full py-3 bg-[#C8B6FF] font-bold" data-testid="calm-save-session">
              <Save className="w-4 h-4 inline" /> Spara & stäng
            </button>
            <button onClick={onClose} className="neo-btn w-full py-2 bg-white text-sm" data-testid="calm-close-without-save">
              Stäng utan att spara
            </button>
            <p className="text-[10px] text-center text-gray-500 px-2">
              Du valde att ta hand om dig själv. Det är modigt. 🌸
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ToolBtn({ onClick, emoji, title, sub, testid, bg }) {
  return (
    <button
      onClick={onClick}
      className="neo-btn p-3 text-left flex flex-col items-start gap-0.5"
      style={{ backgroundColor: bg }}
      data-testid={testid}
    >
      <span className="text-2xl">{emoji}</span>
      <span className="font-bold text-sm font-['Fredoka']">{title}</span>
      <span className="text-[10px] opacity-70">{sub}</span>
    </button>
  );
}
